import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaPlus, FaBell, FaSearch } from "react-icons/fa";
import { Button } from 'components/common';
import { getSites, getNotificationRules, updateNotificationRules, searchUsers } from "../common/settingsApi";
import MultiUserPicker from "../common/MultiUserPicker";

// Rule định tuyến thông báo: mỗi site → kế toán To + CC riêng, nhận thông báo sau khi
// Manager duyệt (phiếu do user thuộc site đó lập). Email gộp: To = danh sách To,
// CC = danh sách CC (+ người lập + line manager), đính kèm PDF/Excel theo mẫu.
//
// UX: chỉ hiển thị các site ĐÃ cấu hình (dạng hàng). Nút "Add site" cho phép
// thêm site mới từ danh sách site còn lại — mở rộng dần khi tổ chức có thêm site.
const EMPTY = { to: [], cc: [] };
const cleanUsers = (arr) => (Array.isArray(arr) ? arr.filter((u) => u && u.id) : []);

// Chuyển payload server → { map, ids } (site có ít nhất 1 To hoặc CC).
const fromPayload = (data) => {
  const m = {};
  const ids = [];
  (data || []).forEach((r) => {
    const to = [];
    const cc = cleanUsers([...(r.to_users || []), ...(r.cc_users || [])]);
    if (to.length || cc.length) { m[r.site_id] = { to, cc }; ids.push(r.site_id); }
  });
  return { m, ids };
};

const NotificationRulesPanel = () => {
  const [sites, setSites] = useState([]);          // tất cả site: [{ value, label }]
  const [map, setMap] = useState({});              // site_id -> { to: [{id,email,name}], cc: [...] }
  const [rows, setRows] = useState([]);            // site_id[] — các site đang hiển thị
  const [originalIds, setOriginalIds] = useState([]); // site_id[] có rule lúc load (để clear khi bỏ)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);     // mở dropdown "Add site"
  const [siteQ, setSiteQ] = useState("");          // filter trong dropdown Add site
  const addRef = useRef(null);

  // Tải một lần khi mở tab.
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [siteRes, ruleRes] = await Promise.all([getSites(), getNotificationRules()]);
        setSites(siteRes.data || []);
        const { m, ids } = fromPayload(ruleRes.data);
        setMap(m);
        setRows(ids);
        setOriginalIds(ids);
      } catch (e) {
        toast.error(e?.response?.data?.error || "Failed to load notification rules.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Đóng dropdown Add site khi click ngoài.
  useEffect(() => {
    if (!adding) return undefined;
    const h = (e) => { if (!addRef.current?.contains(e.target)) setAdding(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [adding]);

  // Cập nhật 1 loại (to|cc) của 1 site.
  const setKind = (siteId, kind, users) =>
    setMap((s) => ({ ...s, [siteId]: { ...(s[siteId] || EMPTY), [kind]: users } }));

  const siteLabel = (id) => sites.find((s) => s.value === id)?.label || `Site #${id}`;

  const countOf = (id) => (map[id]?.to?.length || 0) + (map[id]?.cc?.length || 0);

  const availableSites = useMemo(() => {
    const term = siteQ.trim().toLowerCase();
    return sites
      .filter((s) => !rows.includes(s.value))
      .filter((s) => !term || String(s.label).toLowerCase().includes(term));
  }, [sites, rows, siteQ]);

  const addSite = (siteId) => {
    setRows((r) => (r.includes(siteId) ? r : [...r, siteId]));
    setAdding(false);
    setSiteQ("");
  };

  const removeRow = (siteId) => {
    setRows((r) => r.filter((id) => id !== siteId));
    setMap((m) => { const next = { ...m }; delete next[siteId]; return next; });
  };

  const assignedCount = useMemo(
    () => rows.filter((id) => (map[id]?.to?.length || 0) + (map[id]?.cc?.length || 0) > 0).length,
    [rows, map]
  );

  const save = async () => {
    setSaving(true);
    try {
      // Site đang hiển thị → gửi kèm to/cc. Site đã bỏ khỏi list (nhưng trước có
      // rule) → gửi mảng rỗng để backend xóa.
      const removed = originalIds.filter((id) => !rows.includes(id));
      const rules = [
        ...rows.map((id) => ({
          site_id: id,
          to_user_ids: (map[id]?.to || []).map((u) => u.id),
          cc_user_ids: (map[id]?.cc || []).map((u) => u.id),
        })),
        ...removed.map((id) => ({ site_id: id, to_user_ids: [], cc_user_ids: [] })),
      ];
      const res = await updateNotificationRules(rules);
      // Đồng bộ lại từ server để originalIds/rows phản ánh đúng state đã lưu.
      const { m, ids } = fromPayload(res.data);
      // Giữ lại các hàng vừa thêm nhưng chưa chọn ai (chưa persist) để admin chọn tiếp.
      const emptyRows = rows.filter((id) => !ids.includes(id));
      setMap((prev) => ({ ...m, ...Object.fromEntries(emptyRows.map((id) => [id, prev[id] || EMPTY])) }));
      setRows([...ids, ...emptyRows]);
      setOriginalIds(ids);
      toast.success("Notification rules saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-min-h-0">
      {/* Header toolbar: mô tả + tiến độ (trái) — Add site + Save (phải) */}
      <div className="tw-flex tw-items-start tw-gap-3 tw-mb-4 tw-flex-shrink-0">
        <span className="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-md tw-bg-geist-100 tw-text-geist-accent">
          <FaBell size={15} />
        </span>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">Accountant notifications</span>
            <span className="tw-text-[11px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2">
              {assignedCount} configured
            </span>
          </div>
          <p className="tw-text-xs tw-text-geist-fg-3 tw-m-0 tw-mt-0.5">
            The Accountant reviewer comes from the signer configuration in Settings. These site recipients receive CC notifications only.
          </p>
        </div>

        {/* Add site — dropdown chọn site còn lại */}
        <div ref={addRef} className="tw-relative tw-flex-shrink-0">
          <Button
            variant="secondary" size="sm" leftIcon={FaPlus}
            disabled={loading || saving}
            onClick={() => setAdding((v) => !v)}
          >
            Add site
          </Button>
          {adding && (
            <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-64 tw-z-50 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden">
              <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg-2 tw-rounded-geist-sm tw-px-2.5 tw-py-1.5">
                  <FaSearch size={11} className="tw-text-geist-fg-3 tw-flex-shrink-0" />
                  <input
                    autoFocus value={siteQ} onChange={(e) => setSiteQ(e.target.value)}
                    placeholder="Search sites…"
                    className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
                  />
                </div>
              </div>
              <div className="tw-max-h-60 tw-overflow-y-auto tw-py-1">
                {availableSites.length === 0 ? (
                  <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-text-center">
                    {sites.length === rows.length ? "All sites added." : "No sites match."}
                  </div>
                ) : (
                  availableSites.map((s) => (
                    <div
                      key={s.value} role="button" tabIndex={0}
                      onClick={() => addSite(s.value)}
                      className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-cursor-pointer hover:tw-bg-geist-100 tw-transition-colors"
                    >
                      <FaPlus size={9} className="tw-text-geist-fg-3 tw-flex-shrink-0" />
                      <span className="tw-text-[13px] tw-text-geist-fg tw-truncate">{s.label}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <Button
          variant="primary" size="sm" leftIcon={FaSave}
          disabled={saving || loading}
          onClick={save}
          className="tw-flex-shrink-0"
        >
          {saving ? "Saving…" : "Save rules"}
        </Button>
      </div>

      {/* List — chỉ các site đã thêm. Tên site trái, 2 picker (To/Cc) phải, nút xóa hàng. */}
      {loading ? (
        <div className="tw-py-12 tw-text-center tw-text-geist-fg-3 tw-text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="tw-rounded-geist-md tw-border tw-border-dashed tw-border-geist-border-2 tw-py-12 tw-px-4 tw-text-center">
          <div className="tw-text-sm tw-text-geist-fg-2 tw-font-medium">No sites configured yet</div>
          <div className="tw-text-xs tw-text-geist-fg-3 tw-mt-1">
            Click <span className="tw-font-semibold">Add site</span> to route notifications for a site.
          </div>
        </div>
      ) : (
        <div className="tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-bg-white tw-divide-y tw-divide-geist-border">
          {rows.map((id) => {
            const cur = map[id] || EMPTY;
            const total = countOf(id);
            return (
              <div key={id} className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-2 sm:tw-gap-4 tw-px-4 tw-py-3">
                <div className="sm:tw-w-56 tw-flex-shrink-0 tw-flex tw-items-center tw-gap-2 tw-min-w-0 sm:tw-pt-2">
                  <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl tw-flex-shrink-0 ${total ? "tw-bg-geist-success" : "tw-bg-geist-border-2"}`} />
                  <span className="tw-text-[13px] tw-font-medium tw-text-geist-fg tw-truncate">{siteLabel(id)}</span>
                  {total > 0 && (
                    <span className="tw-text-[10px] tw-text-geist-fg-3 tw-flex-shrink-0">{total}</span>
                  )}
                </div>
                <div className="tw-flex-1 tw-min-w-0 tw-space-y-2.5">
                  <div>
                    <div className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-mb-1">Cc</div>
                    <MultiUserPicker
                      value={cur.cc}
                      onChange={(users) => setKind(id, "cc", users)}
                      fetcher={searchUsers}
                      placeholder="Add Cc recipients…"
                    />
                  </div>
                </div>
                <button
                  type="button" title="Remove site"
                  onClick={() => removeRow(id)}
                  className="tw-flex-shrink-0 tw-mt-1 tw-h-7 tw-w-7 tw-inline-flex tw-items-center tw-justify-center tw-rounded-geist-sm tw-border-0 tw-bg-transparent tw-text-geist-fg-3 hover:tw-bg-geist-100 hover:tw-text-geist-error tw-transition-colors"
                >
                  <i className="bi bi-trash tw-text-[13px]" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationRulesPanel;
